/** The person record's cards (plan "Page anatomy" → Cards): a `ViewLayout` over the user
 *  registry (`userSections`) — Contacts, Reachability, Identity, Access, Origin, Technical.
 *
 *  One column below 1200 px of content, in card order; from 1200 px Contacts full width,
 *  then Reachability + Origin | Identity + Access, Technical full width. The records core's
 *  width rule (`spineColumns`): the layout follows the width the cards have in EDIT mode,
 *  in both modes, and each card's field grid (and the contacts rows' layout) is pinned to
 *  it — entering edit mode, which docks the ledger, re-lays nothing out.
 *
 *  Beside their catalog fields some cards state facts the catalog does not carry: Access
 *  the sign-in facts (links to the tabs that own them), Origin the join date and what the
 *  app reports, Technical the internal properties (collapsed). */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Link } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { UserRecordSection } from '@/api/userRecord';
import type { ConsoleUserDetail } from '@/api/users';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { contactsLayout } from '@/features/records/fields/widgets/ContactsWidget';
import { FIELD_GRID_VAR, fieldGridColumns } from '@/features/records/panels/fieldGrid';
import { PANEL_GAP_PX, spineColumns } from '@/features/records/views/spineColumns';
import type { ViewSection } from '@/features/records/views/types';
import { formatZoned } from '@/utils/zonedTime';

import { tripCountActions, urlActions } from '../../userLinks';
import { ContactsCard } from '../contacts/ContactsCard';
import { ReachabilityCard } from '../ReachabilityCard';
import { USER_RECORD_REGISTRY } from '../registry';
import { userSections, type UserRecordData } from '../views/userLayout';

import { copyable, userLinks, yesNo, type UserDisplayContext } from './displays';
import { FactGrid, type Fact } from './FactGrid';
import { UserSectionPanel } from './UserSectionPanel';

/** The page tabs a fact links to. */
export type CardTab = 'sessions' | 'permissions' | 'authentication' | 'bookings';

export interface UserCardsViewProps {
  data: UserRecordData;
  /** The page content's width (ledger column included); 0 = unmeasured. */
  width: number;
  /** A page tab's href, or null when the operator does not see that tab. */
  tabHref: (tab: CardTab) => string | null;
}

function utc(ts: string | null | undefined): string | null {
  return ts ? formatZoned(ts, 'UTC', { weekday: false }) : null;
}

function tabLink(href: string | null, text: ReactNode): ReactNode {
  return href ? (
    <Link component={RouterLink} to={href} underline="hover">
      {text}
    </Link>
  ) : (
    text
  );
}

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

const MFA_KIND: Record<string, string> = { totp: 'authenticator app', sms: 'SMS', email: 'email' };

export function accessFacts(u: ConsoleUserDetail, tabHref: UserCardsViewProps['tabHref']): Fact[] {
  const facts: Fact[] = [];
  if (u.kind === 'guest') {
    const ml = u.magic_link;
    facts.push({
      key: 'magic_link',
      label: 'Magic link',
      value: !ml?.present ? 'None' : ml.expires ? `Valid until ${utc(ml.expires)}` : 'Valid, no expiry',
    });
  } else {
    const kinds = [...new Set(u.devices.filter((d) => d.confirmed).map((d) => MFA_KIND[d.kind] ?? d.kind))];
    facts.push({
      key: 'mfa',
      label: 'MFA',
      value: tabLink(tabHref('authentication'), u.mfa_enrolled ? `Enrolled${kinds.length ? ` · ${kinds.join(', ')}` : ''}` : 'Not enrolled'),
    });
  }
  const n = u.active_tokens_count;
  facts.push({ key: 'sessions', label: 'Sessions', value: tabLink(tabHref('sessions'), `${n} active`) });
  facts.push({ key: 'last_login', label: 'Last login', value: utc(u.last_login) ?? 'Never signed in' });
  if (u.kind === 'system') {
    facts.push({
      key: 'groups',
      label: 'Console groups',
      value: tabLink(tabHref('permissions'), u.groups.length ? u.groups.map((g) => g.name).join(', ') : 'None'),
    });
    facts.push({
      key: 'console_permissions',
      label: 'Console permissions',
      value: tabLink(tabHref('permissions'), String(u.permissions_count ?? u.permissions.length)),
    });
    if ((u.pending_invite_count ?? 0) > 0) {
      facts.push({
        key: 'invite',
        label: 'Console invite',
        value: tabLink(tabHref('authentication'), `${u.pending_invite_count} pending`),
      });
    }
  }
  if (u.kind === 'staff' && u.tour_operator_users.length > 0) {
    facts.push({
      key: 'roles',
      label: 'Staff roles',
      wide: true,
      value: u.tour_operator_users
        .map((t) => `${t.roles.map((r) => r.name).join(', ') || 'No role'} @ ${t.tour_operator.code || t.tour_operator.name}`)
        .join(' · '),
    });
  }
  if (u.access_groups.length > 0) {
    facts.push({
      key: 'access_groups',
      label: 'Access groups',
      wide: true,
      value: tabLink(tabHref('permissions'), u.access_groups.map((g) => g.name).join(', ')),
    });
  }
  if (u.is_superuser) facts.push({ key: 'superuser', label: 'Superuser', value: 'Yes' });
  return facts;
}

export function originFacts(u: ConsoleUserDetail): { vendor: Fact[]; system: Fact[] } {
  const a = u.attributes;
  return {
    vendor: [
      { key: 'location_status', label: 'Location status', value: a.location_status },
      { key: 'airbnb_user_id', label: 'Airbnb user ID', value: copyable(a.airbnb_user_id, 'Copy Airbnb user ID') },
    ],
    system: [{ key: 'date_joined', label: 'Joined', value: utc(a.date_joined ?? u.date_joined) }],
  };
}

export function technicalFacts(u: ConsoleUserDetail, tabHref: UserCardsViewProps['tabHref']): Fact[] {
  const i = u.internal;
  const links = userLinks(u);
  return [
    {
      key: 'main_office_conversation',
      label: 'Main office conversation',
      value: copyable(i.main_office_conversation, 'Copy conversation SID'),
      help: 'The Twilio conversation of the user’s in-app chat with the tour operator’s office.',
    },
    {
      key: 'deep_link',
      label: 'Deep link',
      value: i.deep_link ? <ValueActions actions={urlActions(i.deep_link, 'Open deep link')}>{i.deep_link}</ValueActions> : null,
      help: 'Opens the app on this user’s trips.',
      wide: true,
    },
    {
      key: 'trip_count',
      label: 'Trip count',
      value:
        i.trip_count > 0
          ? tabLink(tabHref('bookings'), <ValueActions actions={tripCountActions(u.id, links)}>{i.trip_count}</ValueActions>)
          : String(i.trip_count),
      help: 'Bookings counted for the user (the cached count the app shows).',
    },
    { key: 'bypass_magic_auth', label: 'Bypass magic auth', value: yesNo(i.bypass_magic_auth), help: 'Signs in without a magic-link check (test accounts).' },
    { key: 'exclude_from_chat', label: 'Excluded from chat', value: yesNo(i.exclude_from_chat), help: 'Never placed in a trip’s chat.' },
    { key: 'exclude_from_group_chat', label: 'Excluded from group chat', value: yesNo(i.exclude_from_group_chat), help: 'Kept out of group chats only.' },
    { key: 'has_admin_access', label: 'Has admin access', value: yesNo(i.has_admin_access), help: 'The dashboard admin flag, as permissions resolve it.' },
    { key: 'is_admin', label: 'Is admin', value: yesNo(i.is_admin), help: 'The raw admin role flag.' },
    { key: 'is_guest', label: 'Is guest', value: yesNo(i.is_guest), help: 'The raw guest flag.' },
    { key: 'is_rbac_user', label: 'Is RBAC user', value: yesNo(i.is_rbac_user), help: 'Permissions come from access groups (RBAC).' },
    { key: 'is_tour_operator_staff', label: 'Is tour operator staff', value: yesNo(i.is_tour_operator_staff), help: 'Has a staff link to a tour operator.' },
    { key: 'sms_is_undeliverable', label: 'SMS undeliverable', value: yesNo(i.sms_is_undeliverable), help: 'Twilio reported the number as undeliverable.' },
    { key: 'sms_allowed_via_twilio', label: 'SMS allowed via Twilio', value: yesNo(i.sms_allowed_via_twilio), help: 'The tenant’s Twilio setup allows SMS to this user.' },
    { key: 'whatsapp_allowed_via_twilio', label: 'WhatsApp allowed via Twilio', value: yesNo(i.whatsapp_allowed_via_twilio), help: 'The tenant’s Twilio setup allows WhatsApp to this user.' },
    { key: 'whatsapp_status', label: 'WhatsApp status', value: i.whatsapp_status, help: 'Whether the number is known to have WhatsApp.' },
    { key: 'raw_roles', label: 'Role flags', value: i.roles.length ? i.roles.join(', ') : null, help: 'The raw role names the permission code reads.', wide: true },
    { key: 'user_id', label: 'User ID', value: <ValueActions actions={[act.copy(String(u.id), 'Copy id')]}>{u.id}</ValueActions> },
  ];
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function TechnicalCard({ section, display, facts }: { section: ViewSection<UserRecordSection>; display: UserDisplayContext; facts: Fact[] }) {
  const [open, setOpen] = useState(false);
  const shown = open ? section : { ...section, blocks: [] };
  return (
    <UserSectionPanel
      section={shown}
      display={display}
      meta={
        <Button
          size="small"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          data-technical-toggle
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      }
      {...(open ? { afterSystem: <FactGrid facts={facts} /> } : {})}
    />
  );
}

export function UserCardsView({ data, width, tabHref }: UserCardsViewProps) {
  const { editWidth, columns } = spineColumns(width);
  const twoColumns = columns === 2;
  const { catalog, editContext, detail } = data;
  const sections = useMemo(
    () => userSections(USER_RECORD_REGISTRY, { catalog, editContext, detail }),
    [catalog, editContext, detail],
  );
  const display = useMemo<UserDisplayContext>(
    () => ({ detail, editContext, links: userLinks(detail) }),
    [detail, editContext],
  );
  const by = new Map(sections.map((s) => [s.id, s]));
  const pin = (panelWidth: number): Record<string, string> =>
    width > 0 ? { [FIELD_GRID_VAR]: fieldGridColumns(panelWidth) } : {};
  const columnWidth = twoColumns ? (editWidth - PANEL_GAP_PX) / 2 : editWidth;
  const origin = originFacts(detail);

  const card = (id: UserRecordSection): ReactNode => {
    const section = by.get(id);
    if (!section) return null;
    switch (id) {
      case 'contacts':
        return <ContactsCard key={id} section={section} display={display} layout={contactsLayout(width > 0 ? editWidth : 0)} />;
      case 'reachability':
        return <ReachabilityCard key={id} user={detail} section={section} />;
      case 'access':
        return <UserSectionPanel key={id} section={section} display={display} afterSystem={<FactGrid facts={accessFacts(detail, tabHref)} />} />;
      case 'origin':
        return (
          <UserSectionPanel
            key={id}
            section={section}
            display={display}
            afterVendor={<FactGrid facts={origin.vendor} />}
            afterSystem={<FactGrid facts={origin.system} />}
          />
        );
      case 'technical':
        return <TechnicalCard key={id} section={section} display={display} facts={technicalFacts(detail, tabHref)} />;
      default:
        return <UserSectionPanel key={id} section={section} display={display} />;
    }
  };
  const column = (ids: UserRecordSection[], vars: Record<string, string>) => (
    <Box sx={{ display: 'grid', gap: `${PANEL_GAP_PX}px`, minWidth: 0, alignContent: 'start', ...vars }}>{ids.map(card)}</Box>
  );

  return (
    <Box data-user-cards data-columns={twoColumns ? 2 : 1} sx={{ display: 'grid', gap: `${PANEL_GAP_PX}px` }}>
      {twoColumns ? (
        <>
          {column(['contacts'], pin(editWidth))}
          <Box sx={{ display: 'grid', gap: `${PANEL_GAP_PX}px`, gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', alignItems: 'start' }}>
            {column(['reachability', 'origin'], pin(columnWidth))}
            {column(['identity', 'access'], pin(columnWidth))}
          </Box>
          {column(['technical'], pin(editWidth))}
        </>
      ) : (
        column(['contacts', 'reachability', 'identity', 'access', 'origin', 'technical'], pin(editWidth))
      )}
    </Box>
  );
}
